import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Activity, AlertTriangle, ArrowLeft, CheckCircle, ChevronRight, Clock,
  Eye, HelpCircle, Info, Loader2, RefreshCw, Shield, ShieldAlert,
  ShieldCheck, ShieldQuestion, Video, XCircle, Zap, Server,
  Image, TrendingDown, TrendingUp, Wifi, WifiOff, GraduationCap, Target
} from 'lucide-react';
import { toast } from 'react-toastify';
import fetchWithTenant from '../utils/fetchWithTenant';
import BaselineHeatmap from '../components/BaselineHeatmap';
import ThumbnailTimeline from '../components/ThumbnailTimeline';
import InfrastructureChain from '../components/InfrastructureChain';
import RaLicenseBanner from '../components/RaLicenseBanner';
import '../styles/v2/pages/recording-assurance.css';

const STATUS_CONFIG = {
  VERIFIED: { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.12)', icon: ShieldCheck, label: 'Verified recording' },
  DEGRADED: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)', icon: ShieldAlert, label: 'Degraded' },
  AT_RISK:  { color: '#f97316', bg: 'rgba(249, 115, 22, 0.12)', icon: AlertTriangle, label: 'At Risk' },
  FAILED:   { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)', icon: XCircle, label: 'Recording failed' },
  UNKNOWN:  { color: '#64748b', bg: 'rgba(100, 116, 139, 0.12)', icon: ShieldQuestion, label: 'Unknown' },
};

const CHECK_RESULT_CONFIG = {
  PASS:    { color: '#22c55e', label: 'Pass' },
  FAIL:    { color: '#ef4444', label: 'Fail' },
  ERROR:   { color: '#f97316', label: 'Error' },
  TIMEOUT: { color: '#64748b', label: 'Timeout' },
};
// Context-aware result display for layer cards
function getLayerResult(checkResult, checkType, recordingMode, currentStatus) {
  const isMotionIdle = (recordingMode === 'MOTION' && currentStatus === 'UNKNOWN') ||
    (recordingMode === 'SCHEDULED' && currentStatus === 'UNKNOWN');

  if (isMotionIdle && checkResult === 'FAIL' && (checkType === 'TIMELINE_GAP' || checkType === 'THUMBNAIL_SPOT')) {
    return { color: '#0ea5e9', label: 'No motion' };
  }
  return CHECK_RESULT_CONFIG[checkResult] || { color: '#64748b', label: checkResult };
}


const GAP_SEVERITY_CONFIG = {
  MINOR:    { color: '#f59e0b', label: 'Minor (<5 min)' },
  MODERATE: { color: '#f97316', label: 'Moderate (5-30 min)' },
  MAJOR:    { color: '#ef4444', label: 'Major (30 min - 4h)' },
  CRITICAL: { color: '#dc2626', label: 'Critical (>4h)' },
};

const TOOLTIPS = {
  confidence: 'Overall recording confidence score (0-100%). Combines timeline analysis (50%), thumbnail verification (30%), and VMS server health (20%). Scores below 70% trigger alerts.',
  timeline: 'Checks the VMS for actual recorded time periods and compares them against what should be recording. This is the most definitive check -- it directly answers "does footage exist?"',
  thumbnail: 'Requests a snapshot image from 5 minutes ago. If the VMS returns a real image, recording existed at that time. A quick proof-of-recording that catches frozen or black feeds.',
  vmsHealth: 'Monitors the VMS server itself: CPU load, storage capacity, camera connectivity. A healthy server does not guarantee recording, but an unhealthy server is a leading indicator of failures.',
  gap: 'A gap is a period where the camera should have been recording but no footage was found. Gaps are classified by duration: Minor (<5 min), Moderate (5-30 min), Major (30 min-4h), Critical (>4h).',
  neverFalseGreen: 'Recording Assurance follows the "Never False Green" principle. If any check fails or times out, the system will show UNKNOWN or a reduced score rather than falsely reporting everything is OK.',
};

/**
 * Analyze check results and generate a root cause assessment.
 */
function analyzeRootCause(checks, recordingMode, currentStatus) {
  if (!checks || checks.length === 0) return null;

  // Motion camera: always explain the recording mode first
  if (recordingMode === 'MOTION') {
    // Check if there's actual activity or not
    const latest = {};
    for (const check of checks) {
      if (!latest[check.checkType] || new Date(check.checkedAt) > new Date(latest[check.checkType].checkedAt)) {
        latest[check.checkType] = check;
      }
    }
    const timelineFail = latest.TIMELINE_GAP && (latest.TIMELINE_GAP.result === 'FAIL' || latest.TIMELINE_GAP.result === 'ERROR');
    const thumbnailFail = latest.THUMBNAIL_SPOT && (latest.THUMBNAIL_SPOT.result === 'FAIL' || latest.THUMBNAIL_SPOT.result === 'ERROR');
    const healthOk = latest.VMS_HEALTH && latest.VMS_HEALTH.result === 'PASS';

    if (timelineFail || thumbnailFail) {
      return {
        level: 'info',
        title: 'Motion-activated camera',
        details: [
          'This is a motion-activated camera. It only records when movement is detected in the scene.',
          timelineFail ? 'No recorded footage was found for the most recent check window. This is expected when no motion is present.' : 'Some recorded footage was found, indicating motion was detected.',
          thumbnailFail ? 'No archive thumbnail could be retrieved. This is normal when the camera has not recorded recently due to lack of motion.' : 'An archive thumbnail was successfully retrieved.',
          healthOk ? 'The VMS server is healthy and the camera is online. The camera is functioning correctly and will resume recording when motion is detected.' : 'Note: the VMS server may have health issues that could also affect recording.',
          'To verify this camera is responsive, walk in front of it and check back in 10 minutes.'
        ]
      };
    }
    // Motion camera with passing checks = working
    return {
      level: 'success',
      title: 'Motion camera recording normally',
      details: [
        'This is a motion-activated camera and it is currently recording.',
        'Motion has been detected and footage is being written to the archive.',
        'The VMS server is healthy and the camera is functioning correctly.'
      ]
    };
  }

  // Scheduled camera in motion window
  if (recordingMode === 'SCHEDULED') {
    const latest = {};
    for (const check of checks) {
      if (!latest[check.checkType] || new Date(check.checkedAt) > new Date(latest[check.checkType].checkedAt)) {
        latest[check.checkType] = check;
      }
    }
    const timelineFail = latest.TIMELINE_GAP && (latest.TIMELINE_GAP.result === 'FAIL' || latest.TIMELINE_GAP.result === 'ERROR');
    const thumbnailFail = latest.THUMBNAIL_SPOT && (latest.THUMBNAIL_SPOT.result === 'FAIL' || latest.THUMBNAIL_SPOT.result === 'ERROR');

    if (currentStatus === 'UNKNOWN' || (timelineFail && thumbnailFail)) {
      return {
        level: 'info',
        title: 'Scheduled recording camera',
        details: [
          'This camera has a mixed recording schedule: continuous recording during active hours and motion-only recording during off hours.',
          timelineFail ? 'No recorded footage was found for the most recent check window. If this is during the motion-only period, this is expected when no activity is present.' : 'Recorded footage was found for the most recent check window.',
          thumbnailFail ? 'No archive thumbnail could be retrieved. During motion-only periods, this is normal when there is no activity.' : 'An archive thumbnail was successfully retrieved, confirming footage exists.',
          'Recording Assurance evaluates this camera based on which schedule period is currently active.',
          'No action is needed unless you expect continuous recording at this time.'
        ]
      };
    }
  }

  // Get latest of each type
  const latest = {};
  for (const check of checks) {
    if (!latest[check.checkType] || new Date(check.checkedAt) > new Date(latest[check.checkType].checkedAt)) {
      latest[check.checkType] = check;
    }
  }

  const timeline = latest.TIMELINE_GAP;
  const thumbnail = latest.THUMBNAIL_SPOT;
  const health = latest.VMS_HEALTH;

  const causes = [];
  let severity = 'info';
  let primaryCause = 'Unable to determine root cause';

  const timelineFail = timeline && (timeline.result === 'FAIL' || timeline.result === 'ERROR');
  const thumbnailFail = thumbnail && (thumbnail.result === 'FAIL' || thumbnail.result === 'ERROR' || thumbnail.result === 'TIMEOUT');
  const healthFail = health && (health.result === 'FAIL' || health.result === 'ERROR');
  const healthPass = health && health.result === 'PASS';

  // Pattern matching for root cause
  if (timelineFail && thumbnailFail && healthPass) {
    severity = 'critical';
    primaryCause = 'Camera-level recording failure';
    causes.push('The VMS server is healthy and all cameras appear online, but this specific camera is not producing recorded footage.');

    const meta = thumbnail?.metadata;
    if (meta?.reason === 'non_image_response') {
      causes.push('The VMS returned a non-image response when asked for an archive thumbnail, confirming no footage is being written for this camera.');
    } else if (meta?.reason === 'no_recording_at_timestamp') {
      causes.push('No recorded footage found at the requested timestamp.');
    }

    causes.push('Possible causes: camera recording license expired or not assigned, recording schedule disabled for this camera, camera feed is corrupted or producing empty frames, storage location for this camera is full or inaccessible, or the camera was recently rebooted and recording has not resumed.');
    causes.push('Recommended action: check the camera\'s recording settings in the VMS directly. Verify the recording license is assigned and the storage location has free space.');

  } else if (timelineFail && thumbnailFail && healthFail) {
    severity = 'critical';
    primaryCause = 'VMS server-level failure';
    causes.push('The VMS server itself is reporting health issues, which is likely causing recording failures across multiple cameras.');

    const healthMeta = health?.metadata;
    if (healthMeta?.issues?.includes('storage_critical')) {
      causes.push('Storage is critically full (>95%). Recording may have stopped due to lack of disk space.');
    }
    if (healthMeta?.issues?.includes('server_offline')) {
      causes.push('One or more VMS servers are offline.');
    }
    causes.push('Recommended action: check VMS server health immediately. Verify storage capacity, server processes, and network connectivity.');

  } else if (timelineFail && !thumbnailFail && healthPass) {
    severity = 'warning';
    primaryCause = 'Intermittent recording gaps';
    causes.push('The VMS was able to produce a thumbnail (recording exists at some points), but timeline analysis found gaps in the recording.');
    causes.push('This often indicates brief recording interruptions caused by network instability, camera firmware issues, or VMS resource contention during peak load.');
    causes.push('Recommended action: monitor over 24 hours. If gaps persist, check the camera\'s network connection and VMS recording queue.');

  } else if (!timelineFail && thumbnailFail && healthPass) {
    severity = 'info';
    primaryCause = 'Thumbnail generation issue (recording likely OK)';
    causes.push('Timeline analysis shows recording exists, but the thumbnail spot-check failed. This is usually a VMS thumbnail generation issue rather than a recording problem.');
    causes.push('Some cameras (especially fisheye or multi-sensor) may not support thumbnail generation at arbitrary timestamps.');

  } else if (healthFail && !timelineFail) {
    severity = 'warning';
    primaryCause = 'VMS server stress (recording still working)';
    causes.push('The VMS server is under stress but recording continues for now. This is an early warning that recording failures may occur if the server condition worsens.');
    causes.push('Recommended action: investigate server health proactively before recording is impacted.');

  } else if (!timelineFail && !thumbnailFail && healthPass) {
    severity = 'success';
    primaryCause = 'All systems operational';
    causes.push('All three verification layers confirm this camera is recording correctly. Timeline shows continuous coverage, thumbnail verification succeeded, and the VMS server is healthy.');
  }

  return { severity, primaryCause, causes };
}

const Tooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <span className="ra-tooltip-wrapper" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <HelpCircle size={14} className="ra-tooltip-icon" />
      {show && <div className="ra-tooltip-popup">{text}</div>}
    </span>
  );
};


const GAP_CAUSE_LABELS = {
  UNKNOWN: 'Unknown cause',
  CAMERA_OFFLINE: 'Camera went offline',
  SERVER_OVERLOAD: 'VMS server overloaded',
  STORAGE_FULL: 'Storage full',
  NETWORK_ISSUE: 'Network interruption',
  VMS_RESTART: 'VMS service restart',
  SCHEDULED_DOWNTIME: 'Scheduled maintenance',
  MOTION_GAP: 'No motion detected',
  MOTION_WINDOW: 'Motion-only window (expected)',
  ARCHIVE_ROTATION: 'VMS archive segment rotation',
  STREAM_RECONNECT: 'Stream reconnection',
  SERVER_EVENT: 'Server-level event (multiple cameras)',
  RECORDING_FAILURE: 'Recording failure',
};

const RecordingAssuranceDetail = () => {
  const { configId } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [deepVerifyResult, setDeepVerifyResult] = useState(null);
  const [deepVerifying, setDeepVerifying] = useState(false);
  const [correlationResult, setCorrelationResult] = useState(null);
  const [correlating, setCorrelating] = useState(false);
  const [qualityResult, setQualityResult] = useState(null);
  const [checkingQuality, setCheckingQuality] = useState(false);

  const runQualityCheck = async () => {
    setCheckingQuality(true);
    try {
      const res = await fetchWithTenant('/api/recording-assurance/quality/' + configId, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        const data = await res.json();
        setQualityResult(data);
        const score = data.quality?.score ?? 0;
        if (score >= 80) toast.success('Image quality: Good (' + score + '%)');
        else if (score >= 50) toast.warning('Image quality: Degraded (' + score + '%)');
        else toast.error('Image quality: Poor (' + score + '%)');
      } else {
        toast.error('Quality check failed');
      }
    } catch (err) {
      toast.error('Quality error: ' + err.message);
    }
    setCheckingQuality(false);
  };

  const runCorrelation = async () => {
    setCorrelating(true);
    try {
      const res = await fetchWithTenant('/api/recording-assurance/correlation/camera/' + configId, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        const data = await res.json();
        setCorrelationResult(data);
        toast.success('Correlation analysis complete');
      } else {
        toast.error('Correlation failed');
      }
    } catch (err) {
      toast.error('Correlation error: ' + err.message);
    }
    setCorrelating(false);
  };

  const deepVerify = async () => {
    setDeepVerifying(true);
    try {
      const res = await fetchWithTenant('/api/recording-assurance/audit/verify/' + configId, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        const data = await res.json();
        setDeepVerifyResult(data);
        toast.success('Deep verification complete: ' + data.crossValidation.verdict);
      } else {
        toast.error('Verification failed');
      }
    } catch (err) {
      toast.error('Verify error: ' + err.message);
    }
    setDeepVerifying(false);
  };
  const [hours, setHours] = useState(24);

  const fetchData = useCallback(async () => {
    try {
      // Fetch config first to get the VMS cameraId
      const configRes = await fetchWithTenant('/api/recording-assurance/config');
      if (configRes.ok) {
        const data = await configRes.json();
        const cam = data.cameras?.find(c => c.id === configId);
        setConfig(cam);

        // Now fetch timeline using the VMS cameraId (not the configId)
        if (cam?.cameraId) {
          const timelineRes = await fetchWithTenant(
            `/api/recording-assurance/cameras/${encodeURIComponent(cam.cameraId)}/timeline?hours=${hours}`
          );
          if (timelineRes.ok) {
            const timelineData = await timelineRes.json();
            setTimeline(timelineData);
          }
        }
      }
    } catch (err) {
      console.error('[RA Detail] Fetch error:', err);
    }
  }, [configId, hours]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchData();
      setLoading(false);
    };
    load();
  }, [fetchData]);

  const triggerCheck = async () => {
    setChecking(true);
    try {
      const res = await fetchWithTenant(`/api/recording-assurance/cameras/${configId}/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        toast.success('Check complete');
        await fetchData();
      } else {
        toast.error('Check failed');
      }
    } catch (err) {
      toast.error('Check failed: ' + err.message);
    }
    setChecking(false);
  };

  if (loading) {
    return (
      <div className="ra-loading">
        <Loader2 className="spin" size={32} />
        <p>Loading camera details...</p>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="ra-loading">
        <ShieldQuestion size={32} />
        <p>Camera not found</p>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/recording-assurance')}>
          <ArrowLeft size={14} /> Back to Dashboard
        </button>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[config.currentStatus] || STATUS_CONFIG.UNKNOWN;
  const StatusIcon = statusConfig.icon;
  const rootCause = timeline ? analyzeRootCause(timeline.checks, config?.recordingMode, config?.currentStatus) : null;

  // Group checks by type for layer breakdown
  const latestChecks = {};
  if (timeline?.checks) {
    for (const check of timeline.checks) {
      if (!latestChecks[check.checkType] || new Date(check.checkedAt) > new Date(latestChecks[check.checkType].checkedAt)) {
        latestChecks[check.checkType] = check;
      }
    }
  }

  return (
    <div className="ra-page">
      <RaLicenseBanner />
      {/* Header */}
      <div className="ra-detail-header">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/recording-assurance')}>
          <ArrowLeft size={14} /> Back
        </button>
        <div className="ra-detail-title">
          <Video size={20} />
          <div>
            <h1>{config.cameraName || config.cameraId}</h1>
            <p className="ra-subtitle">{config.integration?.name} / {config.recordingMode} recording</p>
          </div>
        </div>
        <div className="ra-header-actions">
          <button className="btn btn-ghost btn-sm" onClick={runQualityCheck} disabled={checkingQuality} style={{ marginRight: '8px' }}>
            {checkingQuality ? <><Loader2 size={14} className="spin" /> Analyzing...</> : <><Eye size={14} /> Image Quality</>}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={runCorrelation} disabled={correlating} style={{ marginRight: '8px' }}>
            {correlating ? <><Loader2 size={14} className="spin" /> Correlating...</> : <><Activity size={14} /> Correlate</>}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={deepVerify} disabled={deepVerifying} style={{ marginRight: '8px' }}>
            {deepVerifying ? <><Loader2 size={14} className="spin" /> Verifying...</> : <><Target size={14} /> Deep Verify</>}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={triggerCheck} disabled={checking}>
            <RefreshCw size={14} className={checking ? 'spin' : ''} />
            {checking ? 'Checking...' : 'Check Now'}
          </button>
        </div>
      </div>

      {/* Status Banner */}
      <div className="ra-status-banner" style={{ borderColor: statusConfig.color, background: statusConfig.bg }}>
        <div className="ra-status-banner-left">
          <StatusIcon size={28} style={{ color: statusConfig.color }} />
          <div>
            <span className="ra-status-banner-label" style={{ color: statusConfig.color }}>{statusConfig.label}</span>
            <span className="ra-status-banner-score">Confidence: {config.currentConfidence}%</span>
          </div>
        </div>
        <Tooltip text={TOOLTIPS.confidence} />
        {config.lastCheckedAt && (
          <span className="ra-status-banner-time">
            <Clock size={14} /> Last checked: {new Date(config.lastCheckedAt).toLocaleString()}
          </span>
        )}
      </div>

      {/* What's happening with this camera - plain English */}
      {config && timeline && (
        <div className="ra-card ra-story-card">
          <div className="ra-story-header">
            <Eye size={18} />
            <h3>What's happening with this camera</h3>
          </div>
          <div className="ra-story-grid">
            {/* Left: What your VMS reports */}
            <div className="ra-story-vms">
              <div className="ra-story-label">
                <Server size={14} />
                <span>What your VMS reports</span>
              </div>
              <div className="ra-story-items">
                <div className="ra-story-item">
                  <span className="ra-story-key">Camera status</span>
                  <span className="ra-story-val" style={{ color: '#22c55e' }}>Online</span>
                </div>
                <div className="ra-story-item">
                  <span className="ra-story-key">Recording mode</span>
                  <span className="ra-story-val">{config.recordingMode}</span>
                </div>
                <div className="ra-story-item">
                  <span className="ra-story-key">VMS server</span>
                  <span className="ra-story-val" style={{
                    color: latestChecks['VMS_HEALTH']?.result === 'PASS' ? '#22c55e' : '#f59e0b'
                  }}>
                    {latestChecks['VMS_HEALTH']?.result === 'PASS' ? 'Healthy' : 'Issues detected'}
                  </span>
                </div>
              </div>
              <div className="ra-story-note">
                This is what you'd see if you only looked at your VMS dashboard. Everything might look fine.
              </div>
            </div>

            {/* Divider */}
            <div className="ra-story-divider">
              <div className="ra-story-vs">VS</div>
            </div>

            {/* Right: What RA found */}
            <div className="ra-story-ra">
              <div className="ra-story-label">
                <Shield size={14} />
                <span>What Recording Assurance found</span>
              </div>
              <div className="ra-story-items">
                <div className="ra-story-item">
                  <span className="ra-story-key">Actual recording</span>
                  <span className="ra-story-val" style={{
                    color: (() => {
                      const tl = latestChecks['TIMELINE_GAP'];
                      if (!tl) return '#64748b';
                      const cov = tl.metadata?.coveragePercent;
                      const isMotion = config?.recordingMode === 'MOTION';
                      const isMotionWindow = config?.recordingMode === 'SCHEDULED' && config?.currentStatus === 'UNKNOWN';
                      if ((isMotion || isMotionWindow) && cov < 10) return '#0ea5e9';
                      if (cov >= 80) return '#22c55e';
                      if (cov >= 30) return '#f59e0b';
                      return '#ef4444';
                    })()
                  }}>
                    {(() => {
                      const tl = latestChecks['TIMELINE_GAP'];
                      if (!tl) return 'Checking...';
                      const cov = tl.metadata?.coveragePercent;
                      const isMotion = config?.recordingMode === 'MOTION';
                      const isMotionWindow = config?.recordingMode === 'SCHEDULED' && config?.currentStatus === 'UNKNOWN';
                      if (cov === undefined) return tl.result === 'PASS' ? 'Recording confirmed' : 'No recording found';
                      if ((isMotion || isMotionWindow) && cov < 10) return 'No motion detected (expected)';
                      return cov + '% of footage exists';
                    })()}
                  </span>
                </div>
                <div className="ra-story-item">
                  <span className="ra-story-key">Archive verification</span>
                  <span className="ra-story-val" style={{
                    color: latestChecks['THUMBNAIL_SPOT']?.result === 'PASS' ? '#22c55e' : (config?.recordingMode === 'MOTION' || (config?.recordingMode === 'SCHEDULED' && config?.currentStatus === 'UNKNOWN')) ? '#0ea5e9' : '#ef4444'
                  }}>
                    {latestChecks['THUMBNAIL_SPOT']?.result === 'PASS'
                      ? 'Real footage confirmed'
                      : (config?.recordingMode === 'MOTION' || (config?.recordingMode === 'SCHEDULED' && config?.currentStatus === 'UNKNOWN'))
                        ? 'No footage expected (motion camera idle)'
                        : 'Could not retrieve footage'}
                  </span>
                </div>
                <div className="ra-story-item">
                  <span className="ra-story-key">Confidence</span>
                  <span className="ra-story-val" style={{
                    color: config.currentConfidence >= 90 ? '#22c55e' : config.currentConfidence >= 70 ? '#f59e0b' : '#ef4444'
                  }}>
                    {config.currentConfidence}%
                  </span>
                </div>
              </div>
              <div className="ra-story-note" style={{
                borderColor: config.currentStatus === 'VERIFIED' ? 'rgba(34,197,94,0.2)' : config.currentStatus === 'FAILED' ? 'rgba(239,68,68,0.2)' : 'rgba(100,116,139,0.2)',
                background: config.currentStatus === 'VERIFIED' ? 'rgba(34,197,94,0.04)' : config.currentStatus === 'FAILED' ? 'rgba(239,68,68,0.04)' : 'rgba(100,116,139,0.04)',
              }}>
                {config.currentStatus === 'VERIFIED' && 'Recording Assurance independently verified that this camera is writing footage to storage. If an incident occurs, the footage should be available.'}
                {config.currentStatus === 'DEGRADED' && 'Recording exists but has some gaps. Footage may be incomplete for certain time periods. Review the gap history below for details.'}
                {config.currentStatus === 'FAILED' && 'Recording Assurance could not verify that this camera is writing footage to storage. If an incident occurs right now, footage may not be available. Investigate immediately.'}
                {config.currentStatus === 'UNKNOWN' && config.recordingMode === 'SCHEDULED' && 'This camera is in its motion-only recording window. Low coverage is expected when there is no activity. Recording Assurance will re-evaluate during the next scheduled recording period.'}
                {config.currentStatus === 'UNKNOWN' && config.recordingMode !== 'SCHEDULED' && 'Recording Assurance does not have enough data to determine if this camera is recording correctly. The system is still learning this camera\'s behavior.'}
                {config.currentStatus === 'AT_RISK' && 'Significant recording gaps detected. This camera may be experiencing intermittent recording failures. Footage availability cannot be guaranteed.'}
              </div>
            </div>
          </div>
        </div>
      )}

            {/* Infrastructure Chain */}
      {config && (
        <InfrastructureChain
          config={config}
          latestChecks={latestChecks}
          correlationResult={correlationResult}
        />
      )}

            {/* Root Cause Analysis */}
      {rootCause && (
        <div className={`ra-card ra-root-cause ra-root-cause-${rootCause.severity}`}>
          <div className="ra-root-cause-header">
            <Zap size={18} />
            <h3>Root Cause Analysis</h3>
            <Tooltip text="Automated analysis based on the combination of all three verification layers. This helps identify whether the issue is at the camera level, server level, or network level." />
          </div>
          <div className="ra-root-cause-primary">{(rootCause.primaryCause || rootCause.title)}</div>
          <ul className="ra-root-cause-details">
            {(rootCause.causes || rootCause.details).map((cause, i) => (
              <li key={i}>{cause}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Deep Verify Results */}
      {deepVerifyResult && (
        <div className={"ra-card ra-root-cause ra-root-cause-" + (deepVerifyResult.crossValidation.isFalsePositive ? 'critical' : deepVerifyResult.crossValidation.isFalseNegative ? 'critical' : 'success')} style={{ marginBottom: '20px' }}>
          <div className="ra-root-cause-header">
            <Target size={18} />
            <h3>Deep Verification Result</h3>
          </div>
          <div className="ra-root-cause-primary">{deepVerifyResult.crossValidation.verdict.replace(/_/g, ' ')}</div>
          <ul className="ra-root-cause-details">
            <li>{deepVerifyResult.crossValidation.verdictDetail}</li>
            <li>Timeline coverage: {deepVerifyResult.crossValidation.timelineCoverage}%</li>
            <li>Thumbnails: {deepVerifyResult.crossValidation.thumbnailHits} found</li>
            <li>VMS healthy: {deepVerifyResult.crossValidation.vmsHealthy ? 'Yes' : 'No'}</li>
          </ul>
        </div>
      )}

            {/* Cross-Domain Correlation Results */}
      {correlationResult && (
        <div className="ra-card" style={{ marginBottom: '20px' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Activity size={18} style={{ color: '#a855f7' }} />
            <h3 style={{ margin: 0, fontSize: '15px', color: 'var(--text-primary)' }}>Cross-Domain Event Correlation</h3>
          </div>

          {/* Root Cause */}
          {correlationResult.rootCause && (
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{
                  fontSize: '12px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px',
                  background: correlationResult.rootCause.severity === 'critical' ? 'rgba(239,68,68,0.15)' :
                    correlationResult.rootCause.severity === 'high' ? 'rgba(249,115,22,0.15)' : 'rgba(14,165,233,0.15)',
                  color: correlationResult.rootCause.severity === 'critical' ? '#ef4444' :
                    correlationResult.rootCause.severity === 'high' ? '#f97316' : '#0ea5e9'
                }}>
                  {correlationResult.rootCause.cause.replace(/_/g, ' ')}
                </span>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  Confidence: {correlationResult.confidence}%
                </span>
              </div>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 8px' }}>
                {correlationResult.rootCause.summary}
              </p>
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.5, margin: 0, fontStyle: 'italic' }}>
                Recommendation: {correlationResult.rootCause.recommendation}
              </p>
            </div>
          )}

          {/* Correlation Chain */}
          {correlationResult.chain && correlationResult.chain.length > 0 && (
            <div style={{ padding: '16px 20px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px', fontWeight: 600 }}>
                Event chain
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                {correlationResult.chain.map((link, i) => (
                  <div key={i} style={{ display: 'flex', gap: '14px', position: 'relative', paddingBottom: i < correlationResult.chain.length - 1 ? '16px' : '0' }}>
                    {/* Timeline connector */}
                    {i < correlationResult.chain.length - 1 && (
                      <div style={{ position: 'absolute', left: '11px', top: '28px', bottom: '0', width: '2px', background: 'var(--border-color)' }} />
                    )}
                    {/* Domain icon */}
                    <div style={{
                      width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: link.severity === 'critical' ? 'rgba(239,68,68,0.15)' :
                        link.severity === 'high' ? 'rgba(249,115,22,0.15)' : 'rgba(14,165,233,0.15)',
                      color: link.severity === 'critical' ? '#ef4444' :
                        link.severity === 'high' ? '#f97316' : '#0ea5e9',
                      fontSize: '11px', fontWeight: 700, zIndex: 1
                    }}>
                      {i + 1}
                    </div>
                    {/* Event details */}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                        <strong style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{link.domain}</strong>
                        {link.timestamp && (
                          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                            {new Date(link.timestamp).toLocaleTimeString()}
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
                        {link.summary}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No correlations found */}
          {correlationResult.chain && correlationResult.chain.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
              No correlated events found in network, server, or VMS data. This appears to be an isolated camera issue.
            </div>
          )}
        </div>
      )}

            {/* Image Quality Analysis Results */}
      {qualityResult && (
        <div className="ra-card" style={{ marginBottom: '20px' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Eye size={18} style={{ color: '#06b6d4' }} />
            <h3 style={{ margin: 0, fontSize: '15px', color: 'var(--text-primary)' }}>Image Quality Analysis</h3>
            <span style={{
              marginLeft: 'auto', fontSize: '13px', fontWeight: 600, padding: '2px 10px', borderRadius: '4px',
              background: (qualityResult.quality?.score >= 80) ? 'rgba(34,197,94,0.12)' :
                (qualityResult.quality?.score >= 50) ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)',
              color: (qualityResult.quality?.score >= 80) ? '#22c55e' :
                (qualityResult.quality?.score >= 50) ? '#f59e0b' : '#ef4444'
            }}>
              {qualityResult.quality?.score >= 80 ? 'Good' : qualityResult.quality?.score >= 50 ? 'Degraded' : 'Poor'} quality
            </span>
          </div>

          {/* Plain English Summary */}
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)' }}>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.7, margin: 0 }}>
              {qualityResult.quality?.score >= 80 && qualityResult.quality?.issues?.length === 0 && (
                <>This camera is producing <strong style={{ color: '#22c55e' }}>clear, usable footage</strong>. The image is sharp and in focus, the scene is unobstructed, the feed is active, and the camera has not been moved from its original position. If an incident occurs, the footage from this camera will be reliable as evidence.</>
              )}
              {qualityResult.quality?.score >= 80 && qualityResult.quality?.issues?.length > 0 && (
                <>Image quality is <strong style={{ color: '#f59e0b' }}>acceptable but has minor concerns</strong>. The footage is usable, but the issues noted below should be addressed to maintain optimal quality.</>
              )}
              {qualityResult.quality?.score >= 50 && qualityResult.quality?.score < 80 && (
                <>Image quality is <strong style={{ color: '#f59e0b' }}>degraded</strong>. Footage from this camera may not be clear enough for identification purposes. Review the issues below and take corrective action.</>
              )}
              {qualityResult.quality?.score < 50 && qualityResult.quality?.score > 0 && (
                <>Image quality is <strong style={{ color: '#ef4444' }}>poor</strong>. Footage from this camera is unlikely to be useful for investigation or evidence. Immediate attention is required.</>
              )}
              {qualityResult.quality?.score === 0 && (
                config?.recordingMode === 'MOTION' || config?.recordingMode === 'SCHEDULED'
                  ? <>This is a <strong style={{ color: '#0ea5e9' }}>{config.recordingMode === 'MOTION' ? 'motion-activated' : 'scheduled'} camera</strong>. No thumbnail could be retrieved, which may simply mean no motion has been detected recently. This is expected behavior when the scene is inactive. The camera will resume recording when motion is detected.</>
                  : <>This camera is <strong style={{ color: '#ef4444' }}>not producing usable footage</strong>. The camera may be offline, obstructed, or experiencing a hardware failure. Investigate immediately.</>
              )}
            </p>
          </div>

          {/* Issues (if any) */}
          {qualityResult.quality?.issues?.length > 0 && (
            <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-color)' }}>
              {qualityResult.quality.issues.map((issue, i) => (
                <div key={i} style={{
                  display: 'flex', gap: '10px', padding: '10px 14px', marginBottom: i < qualityResult.quality.issues.length - 1 ? '8px' : 0,
                  background: issue.severity === 'critical' ? 'rgba(239,68,68,0.06)' : 'rgba(245,158,11,0.06)',
                  border: '1px solid ' + (issue.severity === 'critical' ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)'),
                  borderRadius: '8px'
                }}>
                  <AlertTriangle size={16} style={{
                    color: issue.severity === 'critical' ? '#ef4444' : '#f59e0b',
                    flexShrink: 0, marginTop: '2px'
                  }} />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: issue.severity === 'critical' ? '#ef4444' : '#f59e0b', marginBottom: '2px' }}>
                      {issue.type === 'BLACK_FRAME' ? 'Camera not producing video' :
                       issue.type === 'SEVERE_BLUR' ? 'Image is severely out of focus' :
                       issue.type === 'MODERATE_BLUR' ? 'Image is slightly soft or dirty' :
                       issue.type === 'OBSTRUCTION' ? 'Camera lens appears blocked' :
                       issue.type === 'PARTIAL_OBSTRUCTION' ? 'Possible partial obstruction' :
                       issue.type === 'OVEREXPOSED' ? 'Image is washed out' :
                       issue.type === 'FROZEN_FEED' ? 'Video feed is frozen' :
                       issue.type === 'SCENE_SHIFT' ? 'Camera has been moved' :
                       issue.type.replace(/_/g, ' ')}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                      {issue.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Visual Metrics (collapsed by default for technical users) */}
          {qualityResult.quality?.metrics && (
            <details style={{ padding: '0 20px 16px' }}>
              <summary style={{ fontSize: '12px', color: 'var(--text-muted)', cursor: 'pointer', padding: '12px 0 8px', userSelect: 'none' }}>
                Technical metrics (for advanced users)
              </summary>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                <div style={{ textAlign: 'center', padding: '10px', background: 'var(--bg-primary)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: qualityResult.quality.metrics.sharpness.rating === 'sharp' ? '#22c55e' : qualityResult.quality.metrics.sharpness.rating === 'acceptable' ? '#0ea5e9' : '#ef4444' }}>
                    {qualityResult.quality.metrics.sharpness.laplacianVariance}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Sharpness ({qualityResult.quality.metrics.sharpness.rating})</div>
                </div>
                <div style={{ textAlign: 'center', padding: '10px', background: 'var(--bg-primary)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: qualityResult.quality.metrics.entropy.rating === 'high' || qualityResult.quality.metrics.entropy.rating === 'normal' ? '#22c55e' : '#ef4444' }}>
                    {qualityResult.quality.metrics.entropy.value}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Entropy ({qualityResult.quality.metrics.entropy.rating})</div>
                </div>
                <div style={{ textAlign: 'center', padding: '10px', background: 'var(--bg-primary)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: qualityResult.quality.metrics.brightness.rating === 'normal' ? '#22c55e' : '#f59e0b' }}>
                    {qualityResult.quality.metrics.brightness.mean}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Brightness ({qualityResult.quality.metrics.brightness.rating})</div>
                </div>
                <div style={{ textAlign: 'center', padding: '10px', background: 'var(--bg-primary)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: qualityResult.quality.metrics.colorDistribution.rating === 'diverse' ? '#22c55e' : '#ef4444' }}>
                    {qualityResult.quality.metrics.colorDistribution.uniqueBins}/16
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Color diversity ({qualityResult.quality.metrics.colorDistribution.rating})</div>
                </div>
              </div>
            </details>
          )}

          {/* Feed + Scene Status */}
          <div style={{ padding: '0 20px 12px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {qualityResult.frozenFeed && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: qualityResult.frozenFeed.isFrozen ? '#ef4444' : '#22c55e' }} />
                <span style={{ color: 'var(--text-secondary)' }}>
                  {qualityResult.frozenFeed.isFrozen ? 'Feed is frozen' : 'Feed is active'}
                </span>
              </div>
            )}
            {qualityResult.sceneShift && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: qualityResult.sceneShift.shifted ? '#f59e0b' : '#22c55e' }} />
                <span style={{ color: 'var(--text-secondary)' }}>
                  {qualityResult.sceneShift.shifted ? 'Camera may have moved' : 'Camera position stable'}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

            {/* Visual Proof - Thumbnail Timeline */}
      {config && <ThumbnailTimeline configId={configId} recordingMode={config.recordingMode} />}

            {/* Baseline Heatmap */}
      {config && (
        <BaselineHeatmap
          cameraId={config.cameraId}
          configId={configId}
          recordingMode={config.recordingMode}
        />
      )}

            {/* Layer Breakdown */}
      <div className="ra-layers-grid">
        {/* Timeline Gap Layer */}
        <div className="ra-card ra-layer-card" style={{ order: 2 }}>
          <div className="ra-layer-header">
            <Activity size={18} />
            <span>Layer 2: Timeline analysis</span>
            <Tooltip text={TOOLTIPS.timeline} />
            {latestChecks.TIMELINE_GAP && (() => {
              const lr = getLayerResult(latestChecks.TIMELINE_GAP.result, 'TIMELINE_GAP', config?.recordingMode, config?.currentStatus);
              return <span className="ra-layer-result" style={{ color: lr.color }}>{lr.label}</span>;
            })()}
          </div>
          {latestChecks.TIMELINE_GAP ? (
            <div className="ra-layer-body">
              <div className="ra-layer-stat">
                <span className="ra-layer-stat-value">{latestChecks.TIMELINE_GAP.confidence}%</span>
                <span className="ra-layer-stat-label">Coverage</span>
              </div>
              <div className="ra-layer-stat">
                <span className="ra-layer-stat-value">{latestChecks.TIMELINE_GAP.metadata?.periodCount ?? '?'}</span>
                <span className="ra-layer-stat-label">Periods</span>
              </div>
              <div className="ra-layer-stat">
                <span className="ra-layer-stat-value">{latestChecks.TIMELINE_GAP.metadata?.gapCount ?? 0}</span>
                <span className="ra-layer-stat-label">Gaps</span>
              </div>
              {latestChecks.TIMELINE_GAP.metadata?.totalGapMs > 0 && (
                <div className="ra-layer-stat">
                  <span className="ra-layer-stat-value">{Math.round(latestChecks.TIMELINE_GAP.metadata.totalGapMs / 60000)}m</span>
                  <span className="ra-layer-stat-label">Total Gap</span>
                </div>
              )}
            </div>
          ) : (
            <p className="ra-layer-empty">No data yet</p>
          )}
        </div>

        {/* Thumbnail Layer */}
        <div className="ra-card ra-layer-card" style={{ order: 3 }}>
          <div className="ra-layer-header">
            <Image size={18} />
            <span>Layer 3: Thumbnail spot-check</span>
            <Tooltip text={TOOLTIPS.thumbnail} />
            {latestChecks.THUMBNAIL_SPOT && (
              <span className="ra-layer-result" style={{ color: getLayerResult(latestChecks.THUMBNAIL_SPOT.result, 'THUMBNAIL_SPOT', config?.recordingMode, config?.currentStatus).color }}>
                {getLayerResult(latestChecks.THUMBNAIL_SPOT.result, 'THUMBNAIL_SPOT', config?.recordingMode, config?.currentStatus).label}
              </span>
            )}
          </div>
          {latestChecks.THUMBNAIL_SPOT ? (
            <div className="ra-layer-body">
              <div className="ra-layer-stat">
                <span className="ra-layer-stat-value">{latestChecks.THUMBNAIL_SPOT.confidence}%</span>
                <span className="ra-layer-stat-label">Confidence</span>
              </div>
              {latestChecks.THUMBNAIL_SPOT.metadata?.imageSizeBytes && (
                <div className="ra-layer-stat">
                  <span className="ra-layer-stat-value">{Math.round(latestChecks.THUMBNAIL_SPOT.metadata.imageSizeBytes / 1024)}KB</span>
                  <span className="ra-layer-stat-label">Image Size</span>
                </div>
              )}
              {latestChecks.THUMBNAIL_SPOT.metadata?.reason && (
                <div className="ra-layer-detail">
                  <Info size={12} /> {latestChecks.THUMBNAIL_SPOT.metadata.reason.replace(/_/g, ' ')}
                </div>
              )}
            </div>
          ) : (
            <p className="ra-layer-empty">No data yet</p>
          )}
        </div>

        {/* Image Quality Layer */}
        <div className="ra-card ra-layer-card" style={{ order: 4 }}>
          <div className="ra-layer-header">
            <Eye size={18} />
            <span>Layer 4: Image quality</span>
            <Tooltip text="Analyzes the actual image from the camera for blur, obstruction, frozen feed, and scene changes. Runs automatically every check cycle using the same thumbnail from Layer 3." />
            {latestChecks.IMAGE_QUALITY && (() => {
              const lr = getLayerResult(latestChecks.IMAGE_QUALITY.result, 'IMAGE_QUALITY', config?.recordingMode, config?.currentStatus);
              return <span className="ra-layer-result" style={{ color: lr.color }}>{lr.label}</span>;
            })()}
          </div>
          {latestChecks.IMAGE_QUALITY ? (
            <div className="ra-layer-body">
              <div className="ra-layer-stat">
                <span className="ra-layer-stat-value">{latestChecks.IMAGE_QUALITY.confidence}%</span>
                <span className="ra-layer-stat-label">Quality</span>
              </div>
              <div className="ra-layer-stat">
                <span className="ra-layer-stat-value">{Math.round(latestChecks.IMAGE_QUALITY.metadata?.sharpness || 0)}</span>
                <span className="ra-layer-stat-label">Sharpness</span>
              </div>
              <div className="ra-layer-stat">
                <span className="ra-layer-stat-value">{latestChecks.IMAGE_QUALITY.metadata?.issues?.length || 0}</span>
                <span className="ra-layer-stat-label">Issues</span>
              </div>
            </div>
          ) : (
            <div className="ra-layer-body" style={{ color: 'var(--text-muted)', fontSize: '13px', padding: '12px' }}>
              Waiting for next check cycle...
            </div>
          )}
        </div>

                {/* Baseline Comparison Layer */}
        <div className="ra-card ra-layer-card" style={{ order: 5 }}>
          <div className="ra-layer-header">
            <Activity size={18} />
            <span>Layer 5: Baseline comparison</span>
            <Tooltip text="Compares current recording behavior against the camera's learned historical pattern for this specific hour of the week. Detects anomalies that static thresholds would miss." />
            <span className="ra-layer-result" style={{ color: config?.currentStatus === 'VERIFIED' ? '#22c55e' : config?.currentStatus === 'UNKNOWN' && (config?.recordingMode === 'MOTION' || config?.recordingMode === 'SCHEDULED') ? '#0ea5e9' : '#64748b' }}>
              {config?.currentStatus === 'VERIFIED' ? 'Normal' : config?.currentStatus === 'UNKNOWN' && (config?.recordingMode === 'MOTION' || config?.recordingMode === 'SCHEDULED') ? 'Idle' : 'Learning'}
            </span>
          </div>
          <div className="ra-layer-body" style={{ fontSize: '12px', color: 'var(--text-muted)', padding: '12px' }}>
            {config?.currentStatus === 'VERIFIED' ? 'Current behavior matches historical baseline for this time of week.' :
             config?.currentStatus === 'UNKNOWN' && (config?.recordingMode === 'MOTION' || config?.recordingMode === 'SCHEDULED') ? 'Camera is in expected idle period based on recording schedule.' :
             'Building behavioral baseline. Full comparison available after 7 days.'}
          </div>
        </div>

                {/* VMS Health Layer */}
        <div className="ra-card ra-layer-card" style={{ order: 1 }}>
          <div className="ra-layer-header">
            <Server size={18} />
            <span>Layer 1: VMS server health</span>
            <Tooltip text={TOOLTIPS.vmsHealth} />
            {latestChecks.VMS_HEALTH && (
              <span className="ra-layer-result" style={{ color: CHECK_RESULT_CONFIG[latestChecks.VMS_HEALTH.result]?.color }}>
                {CHECK_RESULT_CONFIG[latestChecks.VMS_HEALTH.result]?.label}
              </span>
            )}
          </div>
          {latestChecks.VMS_HEALTH ? (
            <div className="ra-layer-body">
              <div className="ra-layer-stat">
                <span className="ra-layer-stat-value">{latestChecks.VMS_HEALTH.confidence}%</span>
                <span className="ra-layer-stat-label">Health</span>
              </div>
              {latestChecks.VMS_HEALTH.metadata?.cameras && (
                <div className="ra-layer-stat">
                  <span className="ra-layer-stat-value">{latestChecks.VMS_HEALTH.metadata.cameras.online}/{latestChecks.VMS_HEALTH.metadata.cameras.total}</span>
                  <span className="ra-layer-stat-label">Cameras Online</span>
                </div>
              )}
              {latestChecks.VMS_HEALTH.metadata?.issues?.length > 0 && (
                <div className="ra-layer-detail ra-layer-detail-warning">
                  <AlertTriangle size={12} /> {latestChecks.VMS_HEALTH.metadata.issues.join(', ').replace(/_/g, ' ')}
                </div>
              )}
              {latestChecks.VMS_HEALTH.metadata?.issues?.length === 0 && (
                <div className="ra-layer-detail ra-layer-detail-ok">
                  <CheckCircle size={12} /> No issues detected
                </div>
              )}
            </div>
          ) : (
            <p className="ra-layer-empty">No data yet</p>
          )}
        </div>
      </div>

      {/* Gap History */}
      {timeline?.gaps && timeline.gaps.length > 0 && (
        <div className="ra-card ra-gaps-section">
          <div className="ra-gaps-header">
            <h3>
              <AlertTriangle size={16} />
              Recording Gaps
              <Tooltip text={TOOLTIPS.gap} />
            </h3>
            <div className="ra-time-toggle">
              {[24, 168, 720].map(h => (
                <button key={h} className={`btn btn-ghost btn-xs ${hours === h ? 'active' : ''}`} onClick={() => setHours(h)}>
                  {h === 24 ? '24h' : h === 168 ? '7d' : '30d'}
                </button>
              ))}
            </div>
          </div>
          <div className="ra-gaps-list">
            {timeline.gaps.map((gap, i) => {
              const sevConfig = GAP_SEVERITY_CONFIG[gap.severity] || GAP_SEVERITY_CONFIG.MINOR;
              return (
                <div key={gap.id || i} className="ra-gap-item" style={{ borderLeftColor: sevConfig.color }}>
                  <div className="ra-gap-severity" style={{ color: sevConfig.color }}>
                    <AlertTriangle size={14} />
                    {sevConfig.label}
                  </div>
                  <div className="ra-gap-times">
                    <span>{new Date(gap.gapStart).toLocaleString()}</span>
                    <ChevronRight size={14} />
                    <span>{gap.gapEnd ? new Date(gap.gapEnd).toLocaleString() : 'Ongoing'}</span>
                  </div>
                  <div className="ra-gap-duration">
                    {gap.durationSeconds ? `${Math.round(gap.durationSeconds / 60)} minutes` : 'Unknown duration'}
                  </div>
                  {gap.resolved && (
                    <span className="ra-gap-resolved">
                      <CheckCircle size={12} /> Resolved
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Check History */}
      {timeline?.checks && timeline.checks.length > 0 && (
        <div className="ra-card ra-checks-section">
          <h3>
            <Clock size={16} /> Check History ({hours}h)
          </h3>
          <div className="ra-checks-table">
            <div className="ra-checks-row ra-checks-header-row">
              <span>Time</span>
              <span>Layer</span>
              <span>Result</span>
              <span>Confidence</span>
            </div>
            {timeline.checks.slice().reverse().slice(0, 30).map((check, i) => {
              const resultConfig = CHECK_RESULT_CONFIG[check.result] || CHECK_RESULT_CONFIG.ERROR;
              return (
                <div key={i} className="ra-checks-row">
                  <span>{new Date(check.checkedAt).toLocaleTimeString()}</span>
                  <span className="ra-check-type">{check.checkType.replace(/_/g, ' ')}</span>
                  <span style={{ color: resultConfig.color }}>{resultConfig.label}</span>
                  <span>{check.confidence}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Never False Green Note */}
      <div className="ra-card ra-nfg-note">
        <ShieldCheck size={16} />
        <span>
          <strong>Never False Green</strong> -- If any verification layer cannot confirm recording, this camera will show UNKNOWN or a reduced score. Recording Assurance will never report "all clear" when it cannot prove footage exists.
        </span>
        <Tooltip text={TOOLTIPS.neverFalseGreen} />
      </div>
    </div>
  );
};

export default RecordingAssuranceDetail;
